<template>
  <v-container class="aicrm-main pa-0">
    <div v-if="isLoading" class="text-center my-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-4">데이터를 불러오는 중입니다...</div>
    </div>

    <div v-else-if="error" class="text-center my-8">
      <v-icon large color="error">mdi-alert-circle</v-icon>
      <div class="mt-4 error--text">{{ error.message || '데이터를 불러오는데 실패했습니다.' }}</div>
      <v-btn color="primary" class="mt-4" @click="fetchStoreData">다시 시도</v-btn>
    </div>

    <div v-else>
      <!-- 매장 정보 섹션 -->
      <v-card class="mb-6 store-info" v-if="storeInfo" flat>
        <v-card-title class="text-h5 font-weight-bold">
          {{ storeInfo.store.name }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <v-icon small class="mr-2">mdi-map-marker</v-icon>
            {{ storeInfo.store.address1 }} {{ storeInfo.store.address2 }}
          </div>
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-google-maps</v-icon>
            <v-text-field
              v-model="editingNaverPlaceId"
              dense
              hide-details
              class="mr-2"
              style="max-width: 200px"
              placeholder="네이버 플레이스 ID"
            ></v-text-field>
            <v-btn small color="primary" outlined @click="updateNaverPlaceId" :loading="isUpdating">
              수정
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- 친구 목록 섹션 -->
      <v-card class="mb-6 friends-section" flat>
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <span class="headline">단골 고객</span>
            <span class="ml-2 subtitle-2 grey--text">({{ friends.length }})</span>
          </div>
          <v-btn
            color="primary"
            small
            @click="openAddFriendDialog"
          >
            <v-icon left small>mdi-plus</v-icon>
            추가
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-0">
          <div v-if="friends.length === 0" class="text-center my-4 grey--text">
            아직 등록된 단골 고객이 없습니다.
          </div>
          
          <v-list v-else>
            <v-list-item v-for="friend in friends" :key="friend.id">
              <v-list-item-avatar size="56">
                <span class="white--text">{{ friend.name ? friend.name[0] : 'U' }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="friend-phone">{{ friend.phone || '전화번호 없음' }}</v-list-item-title>
                <v-list-item-subtitle class="friend-name">{{ friend.name || '익명' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>

      <!-- AI 문자메시지 섹션 -->
      <v-card class="mb-6 sms-section" flat>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="headline">AI 문자메시지</span>
          <v-btn
            color="primary"
            small
            @click="generateAiSms"
            :loading="isGeneratingSms"
          >
            <v-icon left small>mdi-message-text</v-icon>
            생성
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div v-if="smsContents.length === 0" class="text-center my-4 grey--text">
            아직 생성된 문자메시지가 없습니다.
          </div>
          
          <v-list v-else class="sms-list">
            <v-list-item v-for="sms in smsContents" :key="sms.id" class="sms-item mb-4">
              <v-list-item-content>
                <div class="sms-bubble">
                  <div class="sms-content">{{ sms.content }}</div>
                  <div class="sms-date caption">{{ formatDate(sms.created_at) }}</div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>

      <!-- 리뷰 섹션 -->
      <v-card class="reviews-section" flat>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="headline">고객 리뷰</span>
          <v-btn
            color="primary"
            small
            @click="scrapeReviews"
            :loading="isScrapingReviews"
          >
            <v-icon left small>mdi-refresh</v-icon>
            AI수집
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div class="review-count px-4">총 {{ reviews.length }}건</div>
          
          <div v-if="reviews.length === 0" class="text-center my-4 grey--text">
            아직 등록된 리뷰가 없습니다.
          </div>
          
          <v-timeline v-else dense>
            <v-timeline-item
              v-for="review in reviews"
              :key="review.id"
              small
              color="#1C160C"
            >
              <div class="review-author mb-1">{{ review.nickname }}</div>
              <div class="review-content">{{ review.content }}</div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </div>

    <!-- 단골고객 추가 다이얼로그 -->
    <v-dialog
      v-model="showAddFriendDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>단골고객 추가</v-card-title>
        <v-card-text>
          <v-form ref="addFriendForm" v-model="isAddFriendFormValid">
            <v-text-field
              v-model="newFriend.phone"
              label="전화번호"
              required
              :rules="[v => !!v || '전화번호를 입력해주세요', v => /^[0-9]{10,11}$/.test(v) || '올바른 전화번호 형식이 아닙니다']"
              placeholder="01012345678"
              hint="- 없이 숫자만 입력"
              persistent-hint
            ></v-text-field>
            <v-text-field
              v-model="newFriend.name"
              label="이름"
              placeholder="홍길동"
              class="mt-4"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showAddFriendDialog = false"
          >
            취소
          </v-btn>
          <v-btn
            color="primary"
            :loading="isAddingFriend"
            :disabled="!isAddFriendFormValid"
            @click="addFriend"
          >
            추가
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 스낵바 추가 -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarText }}
    </v-snackbar>

    <!-- AI SMS 생성 다이얼로그 -->
    <v-dialog
      v-model="showGeneratedSmsDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>AI 문자메시지 미리보기</v-card-title>
        <v-card-text>
          <div class="sms-preview pa-4">
            <div class="sms-bubble">
              <div class="sms-content">{{ generatedSmsContent }}</div>
            </div>
          </div>
          <div class="text-center mt-4">이 내용으로 보내시겠습니까?</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="regenerateAiSms"
            :disabled="isGeneratingSms"
          >
            다시 생성
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmSendSms"
            :loading="isSendingSms"
          >
            이대로 보내기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import aiCrmApi from '@/plugins/api/api/aiCrm'
import moment from 'moment'

export default {
  name: 'AiCrmMain',
  data() {
    return {
      isLoading: true,
      storeInfo: null,
      reviews: [],
      friends: [],
      smsContents: [],
      error: null,
      aiCrmStoreId: null,
      editingNaverPlaceId: '',
      isUpdating: false,
      isScrapingReviews: false,
      isGeneratingSms: false,
      // 단골고객 추가 관련
      showAddFriendDialog: false,
      isAddFriendFormValid: false,
      isAddingFriend: false,
      newFriend: {
        phone: '',
        name: ''
      },
      // 스낵바 관련
      showSnackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      showGeneratedSmsDialog: false,
      generatedSmsContent: '',
      isSendingSms: false
    }
  },
  computed: {
    ...mapState('auth', ['me'])
  },
  methods: {
    formatDate(date) {
      return moment(date).format('YYYY.MM.DD')
    },
    showMessage(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.showSnackbar = true
    },
    async updateNaverPlaceId() {
      if (!this.aiCrmStoreId) return
      
      this.isUpdating = true
      try {
        await aiCrmApi.updateStore(this.aiCrmStoreId, {
          naverplace_id: this.editingNaverPlaceId
        })
        
        this.showMessage('네이버 플레이스 ID가 업데이트되었습니다.')
        
        // 데이터 새로고침
        await this.fetchStoreData()
      } catch (error) {
        console.error('네이버 플레이스 ID 업데이트 중 오류:', error)
        this.showMessage('네이버 플레이스 ID 업데이트에 실패했습니다.', 'error')
      } finally {
        this.isUpdating = false
      }
    },
    async fetchStoreData() {
      console.log('~~~~~~~~~fetchStoreData')
      if (!this.me) return

      this.isLoading = true
      this.error = null
      
      try {
        if (!this.me.store) {
          throw new Error('등록된 매장 정보가 없습니다.')
        }
        
        if (!this.me.store.id) {
          throw new Error('매장 ID가 없습니다.')
        }
        
        // AI CRM 매장 정보 조회
        const { data: storeData } = await aiCrmApi.getStoreByStoreId(this.me.store.id)
        console.log('!!!!!!!storeData', storeData)
        
        // 매장 정보가 없으면 새로 생성
        if (!storeData || !storeData.id) {
          const { data: newStoreData } = await aiCrmApi.createStore({
            store_id: this.me.store.id,
            naverplace_id: ''
          })
          
          this.aiCrmStoreId = newStoreData.id
          this.editingNaverPlaceId = newStoreData.naverplaceId || ''
          this.storeInfo = newStoreData
        } else {
          this.aiCrmStoreId = storeData.id
          this.editingNaverPlaceId = storeData.naverplaceId || ''
          this.storeInfo = storeData
        }

        // 친구 목록 조회
        const { data: friendsData } = await aiCrmApi.getFriends(this.aiCrmStoreId)
        this.friends = friendsData

        // 리뷰 목록 조회
        const { data: reviewsData } = await aiCrmApi.getReviews({ store_id: this.aiCrmStoreId })
        this.reviews = reviewsData.sort((a, b) => 
          moment(b.date).valueOf() - moment(a.date).valueOf()
        )

        // SMS 내용 목록 조회
        const { data: smsContentsData } = await aiCrmApi.getSmsContents(this.aiCrmStoreId)
        this.smsContents = smsContentsData.sort((a, b) => 
          moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
        )

      } catch (error) {
        console.error('데이터 조회 중 오류 발생:', error)
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    openAddFriendDialog() {
      this.newFriend = {
        phone: '',
        name: ''
      }
      this.showAddFriendDialog = true
      if (this.$refs.addFriendForm) {
        this.$refs.addFriendForm.resetValidation()
      }
    },
    async addFriend() {
      if (!this.$refs.addFriendForm.validate()) return
      if (!this.aiCrmStoreId) return

      this.isAddingFriend = true
      try {
        await aiCrmApi.createFriend({
          aicrm_store: this.aiCrmStoreId,
          phone: this.newFriend.phone,
          name: this.newFriend.name || null,
          characteristics: null
        })

        this.showMessage('단골고객이 추가되었습니다.')

        // 다이얼로그 닫기
        this.showAddFriendDialog = false

        // 데이터 새로고침
        await this.fetchStoreData()
      } catch (error) {
        console.error('단골고객 추가 중 오류:', error)
        this.showMessage('단골고객 추가에 실패했습니다.', 'error')
      } finally {
        this.isAddingFriend = false
      }
    },
    async scrapeReviews() {
      if (!this.aiCrmStoreId) return

      if (!this.editingNaverPlaceId) {
        this.showMessage('네이버 플레이스 ID를 먼저 입력해주세요.', 'warning')
        return
      }

      this.isScrapingReviews = true
      try {
        await aiCrmApi.scrapeReviews(this.aiCrmStoreId)
        
        this.showMessage('리뷰가 새로고침되었습니다.')
        
        // 데이터 새로고침
        await this.fetchStoreData()
      } catch (error) {
        console.error('리뷰 스크래핑 중 오류:', error)
        this.showMessage('리뷰 새로고침에 실패했습니다.', 'error')
      } finally {
        this.isScrapingReviews = false
      }
    },
    async generateAiSms() {
      if (!this.aiCrmStoreId) return

      if (!this.editingNaverPlaceId) {
        this.showMessage('네이버 플레이스 ID를 먼저 입력해주세요.', 'warning')
        return
      }

      this.isGeneratingSms = true
      try {
        const { data } = await aiCrmApi.generateAiSms(this.aiCrmStoreId)
        this.generatedSmsContent = data.content
        this.showGeneratedSmsDialog = true
      } catch (error) {
        console.error('AI 문자메시지 생성 중 오류:', error)
        this.showMessage('AI 문자메시지 생성에 실패했습니다.', 'error')
      } finally {
        this.isGeneratingSms = false
      }
    },
    async regenerateAiSms() {
      this.showGeneratedSmsDialog = false
      await this.generateAiSms()
    },
    async confirmSendSms() {
      // TODO: 이대로 보내기 API 구현 후 추가
      this.showGeneratedSmsDialog = false
      this.showMessage('아직 구현되지 않은 기능입니다.', 'info')
    }
  },
  async created() {
    // 초기 데이터 로드
    if (this.me) {
      await this.fetchStoreData()
    }
  }
}
</script>

<style lang="scss" scoped>
.aicrm-main {
  background-color: #FFFFFF;
  min-height: 100vh;
  font-family: "Plus Jakarta Sans", "Noto Sans", sans-serif;

  .v-card {
    box-shadow: none;
    border-radius: 12px;
  }

  .headline {
    font-size: 22px !important;
    font-weight: 700;
    color: #1C160C;
    letter-spacing: -0.015em;
  }

  .v-btn {
    text-transform: none !important;
    letter-spacing: 0.015em !important;
    border-radius: 24px !important;

    &.primary {
      background-color: #019863 !important;
    }
  }

  // 매장 정보 섹션
  .store-info {
    .v-card {
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
      
      .v-card-title {
        color: white;
      }

      .v-card-text {
        color: rgba(255, 255, 255, 0.9);
      }
    }
  }

  // 단골 고객 섹션
  .friends-section {
    .v-card-title {
      padding: 20px 16px 12px;
    }

    .v-btn.v-btn--small {
      min-width: 80px;
      height: 32px;
      font-size: 12px;
    }

    .v-avatar {
      background-color: #019863;
    }

    .friend-name {
      color: #A18249;
      font-size: 14px;
    }

    .friend-phone {
      color: #1C160C;
      font-size: 16px;
      font-weight: 500;
    }
  }

  // AI 문자메시지 섹션
  .sms-section, .sms-preview {
    .sms-list {
      padding: 16px;
    }

    .sms-item {
      padding: 0;
    }

    .sms-bubble {
      background-color: #F5F5F5;
      border-radius: 12px;
      padding: 12px;
      max-width: 80%;
      position: relative;
      
      .sms-content {
        color: #1C160C;
        font-size: 14px;
        line-height: 1.5;
        white-space: pre-wrap;
      }

      .sms-date {
        color: #A18249;
        font-size: 12px;
        margin-top: 4px;
      }
    }
  }

  // 리뷰 섹션
  .reviews-section {
    .review-count {
      color: #A18249;
      font-size: 14px;
      padding: 4px 0;
    }

    .v-timeline {
      padding-top: 0;

      &::before {
        background-color: #E9DFCE;
        width: 1.5px;
      }

      .v-timeline-item {
        .v-timeline-item__body {
          .review-author {
            color: #1C160C;
            font-size: 16px;
            font-weight: 500;
          }

          .review-content {
            color: #A18249;
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style> 